<template>
  <v-form ref="formValid" v-model="valid">
    <v-dialog v-model="dialog" width="800" dark>
      <template v-slot:activator="{ on, attrs }">
        <v-tooltip bottom :disabled="!disableAddDat">
          <template v-slot:activator="{ on: tooltip }">
            <div v-on="tooltip">
              <v-btn
                :id="`add-dat-${datType}`"
                class="ctp-btn mx-3 my-2 px-8"
                rounded
                dark
                :disabled="disableAddDat"
                v-bind="attrs"
                v-on="on"
              >
                {{ $t('map.tolldomains.dats.add_dat_button') }}
              </v-btn>
            </div>
          </template>
          <span>
            {{ $t('map.tolldomains.dats.max_files') }}
          </span>
        </v-tooltip>
      </template>
      <v-card class="dat-container">
        <v-card-title class="title pa-4 pl-11">{{ $t('map.tolldomains.dats.add_dat_modal_title', { type: datType }) }}</v-card-title>
        <v-divider />
        <v-col>
          <div class="mb-1 mt-4 file-name required">{{ $t('map.tolldomains.dats.list.header_dat_name') }}</div>
          <v-text-field
            id="add-dat-datname"
            v-model="fileData.name"
            class="dat-name"
            outlined
            dark
            dense
            hide-details="auto"
            :rules="[...datnameRules, isUniqueString(datnames, 'common.error.forms.inputs.validator.datname_unicity')]"
          />
        </v-col>
        <v-stepper v-model="currentStepIndex">
          <v-stepper-header class="mb-7" dark>
            <template v-for="(title, index) in steps">
              <v-stepper-step
                :key="`step-${index}`"
                :step="index + 1"
              >
                <span>{{ $t(title) }} </span>
              </v-stepper-step>
              <v-divider v-if="index + 1 !== steps.length" :key="index + 1" />
            </template>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content
              v-for="(title, index) in steps"
              :key="`content-step-${index}`"
              :step="index + 1"
              class="pa-0"
            >
              <AddDatDialog
                v-if="currentStepIndex === 1"
                :key="keyObu"
                :index="index"
                :datType="datType"
                :masterAccount="masterAccount"
                :datnames="datnames"
                :datids="datids"
                :disableAddDat="disableAddDat"
                @saveDat="saveDat($event)"
              />

              <AddDatDialog
                v-if="currentStepIndex === 2"
                :key="keyDtco"
                :index="index"
                :datType="datType"
                :masterAccount="masterAccount"
                :datnames="datnames"
                :datids="datids"
                :disableAddDat="disableAddDat"
                @saveDat="saveDat($event)"
              />
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
        <v-divider />
        <v-card-text class="pa-4 pl-11 add-dat-stepper-actions">
          <v-btn
            v-if="currentStepIndex === 1"
            id="add-dat-cancel"
            class="cancel my-0"
            dark
            rounded
            @click="close"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            v-if="currentStepIndex === 2"
            id="add-dat-back"
            class="back my-0"
            dark
            rounded
            @click="goBack"
          >
            {{ $t('common.back') }}
          </v-btn>
          <v-btn
            v-if="currentStepIndex === 1"
            id="add-dat-continue"
            class="ctp-btn"
            dark
            :disabled="!obuFormValid || !fileData || fileData.size > 1032 || !valid"
            :color="colors.secondary"
            @click="goNext"
          >
            {{ $t('common.continue') }}
          </v-btn>
          <v-btn
            v-if="currentStepIndex === 2"
            id="add-dat-confirm"
            class="ctp-btn"
            dark
            :disabled="!dtcoFormValid || !fileData || fileData.size > 1032 || !valid"
            :color="colors.secondary"
            @click="confirm"
          >
            {{ $t('common.confirm') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script lang="ts">
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { colors } from '@/constants/theme/colors';
import Validation from '@/mixins/Validation';
import { MasterAccountInfo } from 'ctp-user-auth-service';
import { max, min, range } from 'lodash';
import { DatePicker } from 'ant-design-vue';
import moment from 'moment';
import AddDatDialog from './AddDatDialog.vue';

@Component({
  components: {
    AddDatDialog,
    DatePicker
  }
})

export default class AddDatDialogStepper extends Mixins(Validation) {
    @Prop()
    private datType!: string;

    @Prop()
    private datnames!: string[];

    @Prop()
    private datids!: number[];

    @Prop()
    private masterAccount!: MasterAccountInfo;

    @Prop()
    private disableAddDat: boolean;

    private currentStepIndex = 1;

    private steps = [
      'OBU5+',
      'DTCO'
    ];

    private async navigate(index: number) {
      this.currentStepIndex = min([index + 1, this.steps.length])!;
    }

    private async goNext() {
      this.currentStepIndex = min([this.currentStepIndex + 1, this.steps.length])!;
    }

    private async goBack() {
      this.currentStepIndex = max([this.currentStepIndex - 1, 1])!;
    }

    private datnameRules = [
      this.requiredRule('common.error.forms.inputs.validator.mandatory')
    ];

    private datuidRules = [
      this.requiredRule('common.error.forms.inputs.validator.mandatory')
    ];

    private datfileRules = [
      this.maxFileSize(1032, 'common.error.forms.inputs.validator.datfile_too_large')
    ];

    private keyObu = false;

    private keyDtco = false;

    private colors = colors;

    private dialog = false;

    private obuFormValid = false;

    private dtcoFormValid = false;

    private valid = false;

    private fileData: any = {
      fileNameObu: '',
      fileNameDtco: '',
      masterId: '',
      uid: null,
      type: '',
      signatureObu: '',
      signatureDtco: '',
      fileDataObu: null,
      fileDataDtco: null,
      name: '',
      size: 0
    };

    private saveDat(fileData: any) {
      this.$set(this.fileData, 'size', fileData.fileData.size);
      this.fileData.masterId = fileData.masterId;
      this.fileData.type = fileData.type;
      this.fileData.uid = fileData.uid;

      if (fileData.index === 0) {
        this.fileData.fileDataObu = fileData.fileData;
        this.fileData.fileNameObu = fileData.fileName;
        this.fileData.signatureObu = fileData.signature;
        this.obuFormValid = true;
      } else if (fileData.index === 1) {
        this.fileData.fileDataDtco = fileData.fileData;
        this.fileData.fileNameDtco = fileData.fileName;
        this.fileData.signatureDtco = fileData.signature;
        this.dtcoFormValid = true;
      }
    }

    private close() {
      (this.$refs.formValid as HTMLFormElement).reset();
      this.dialog = false;
      this.keyObu = !this.keyObu;
      this.keyDtco = !this.keyDtco;
      this.currentStepIndex = 1;
    }

    private confirm() {
      this.$emit('saveDat', this.fileData);
      this.close();
    }

    private disabledDate(current: moment.Moment) {
      return current.isBefore(moment().startOf('day'));
    }

    private disabledTime(current: moment.Moment) {
      if (current && current.isBefore(moment().startOf('day').add(1, 'day'))) {
        if (current.hours() > moment().hours()) {
          return {
            disabledHours: () => range(0, moment().hours())
          };
        }
        if (current.minutes() === 59) {
          return {
            disabledHours: () => range(0, moment().hours() + 1)
          };
        }
        return {
          disabledHours: () => range(0, moment().hours()),
          disabledMinutes: () => range(0, moment().minutes() + 1)
        };
      }
      return {};
    }
}
</script>

<style lang="scss" scoped>
@import './styles/add-dat.scss';
</style>
